import { ReactNode } from 'react';
import { motion } from 'framer-motion';

/**
 * Componente wrapper para itens individuais animados dentro de uma lista
 * 
 * Este componente aplica animações de entrada e saída a cada elemento individual
 * dentro de uma AnimatedList. Funciona em conjunto com AnimatedList para criar
 * o efeito de dispersão/reunião.
 * 
 * @param children - Conteúdo do item que será animado
 * @param className - Classe CSS adicional
 * @param index - Índice do item na lista (usado para stagger)
 */
interface AnimatedItemProps {
  children: ReactNode;
  className?: string;
  index?: number; // Índice para calcular delay adicional se necessário
}

/**
 * Variantes de animação para cada item individual
 * Define o comportamento de entrada (dispersão) e saída (reunião)
 */
const itemVariants = {
  hidden: {
    // Estado inicial: elemento começa no centro/base invisível
    // scale: 0 = tamanho zero (ponto central)
    // opacity: 0 = invisível
    // y: 50 = posicionado 50px abaixo (da base da tela)
    // x: 0 = sem deslocamento horizontal inicial
    scale: 0,
    opacity: 0,
    y: 50, // Entrada vinda da base da tela
    x: 0,
  },
  visible: {
    // Estado visível: elemento na posição final
    // scale: 1 = tamanho normal
    // opacity: 1 = totalmente visível
    // y: 0 = posição final vertical
    // x: 0 = posição final horizontal
    scale: 1,
    opacity: 1,
    y: 0,
    x: 0,
    transition: {
      // Tipo de easing: spring para movimento natural com elasticidade
      type: 'spring',
      // Rigidez da mola: 100 = movimento suave com leve elasticidade
      stiffness: 100,
      // Amortecimento: 15 = reduz oscilação, movimento mais controlado
      damping: 15,
      // Massa: 0.8 = movimento relativamente rápido
      mass: 0.8,
      // Duração base da animação
      duration: 0.4,
    },
  },
  exit: {
    // Estado de saída: elemento se reúne no centro e desaparece
    // scale: 0 = volta a ser um ponto (centro)
    // opacity: 0 = desaparece
    // y: -20 = move ligeiramente para cima enquanto se reúne
    // x: 0 = converge para o centro horizontal
    scale: 0,
    opacity: 0,
    y: -20, // Movimento sutil para cima ao desaparecer
    x: 0,
    transition: {
      // Easing suave para saída
      type: 'spring' as const,
      // Mais rígido na saída para menos bounce
      stiffness: 150,
      damping: 20,
      mass: 0.5,
      duration: 0.3,
    },
  },
};

function AnimatedItem({ children, className = '', index = 0 }: AnimatedItemProps) {
  return (
    <motion.div
      className={className}
      variants={itemVariants}
      // index pode ser usado para delay adicional se necessário
      custom={index}
      // layout: permite que Framer Motion ajuste automaticamente posições
      // durante reordenações (útil para drag & drop ou filtros)
      layout
      // initial, animate e exit são herdados do AnimatedList através do staggerChildren
      // whileHover: efeito opcional ao passar o mouse (desabilitado por padrão)
      // whileHover={{ scale: 1.02 }}
    >
      {children}
    </motion.div>
  );
}

export default AnimatedItem;

